import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";

import CategoryGrid from "./components/CategoryGrid";
import PublicLayout from "./components/PublicLayout";
import TrustedBy from "./components/TrustedBy";
import TopSales from "./components/TopSales";
import FAQ from "./components/FAQ";
import { CartProvider } from "./context/CartContext";
import { AdminAuthProvider } from "./context/AdminAuthContext";
import AdminRoute from "./components/admin/AdminRoute";
import usePageMeta from "./hooks/usePageMeta";
import ProductDetail from "./pages/ProductDetail";
import CategoryPage from "./pages/CategoryPage";
import CatalogPage from "./pages/CatalogPage";
import Offers from "./pages/Offers";
import FloatingWhatsApp from "./components/FloatingWhatsApp";

// Admin pages
import AdminLogin from "./admin/pages/Login";
import AdminDashboard from "./admin/pages/Dashboard";
import AdminProducts from "./admin/pages/Products";
import AdminProductForm from "./admin/pages/ProductForm";
import AdminCategories from "./admin/pages/Categories";
import AdminContent from "./admin/pages/Content";
import AdminSettings from "./admin/pages/Settings";
import AdminQuotes from "./admin/pages/Quotes";
import AdminContacts from "./admin/pages/Contacts";

const Landing = () => {
    const [showScrollArrow, setShowScrollArrow] = useState(false);

    usePageMeta({
        title: "Atelier AX — Accessoires téléphone au Maroc : coques, chargeurs, câbles",
        description: "Coques, chargeurs, câbles, power banks et accessoires téléphone au meilleur prix. Livraison 24-72h partout au Maroc, paiement à la livraison.",
        path: "/",
    });

    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
            const ratio = scrollHeight > 0 ? scrollTop / scrollHeight : 0;
            setShowScrollArrow(ratio > 0.5 && ratio < 0.98);
        };

        handleScroll();
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const scrollToBottom = () => {
        window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: "smooth",
        });
    };

    return (
        <>
            <main>
                <h1 className="sr-only">
                    Atelier AX — Accessoires téléphone au Maroc : coques, chargeurs, câbles
                </h1>
                <CategoryGrid />
                <TopSales />
                <TrustedBy />
                <FAQ />
            </main>

            {showScrollArrow && (
                <button
                    type="button"
                    onClick={scrollToBottom}
                    aria-label="Descendre en bas de la page"
                    className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-12 h-12 rounded-full bg-[#1C1C1E]/90 text-white flex items-center justify-center shadow-xl hover:bg-[#1C1C1E] transition"
                >
                    <ChevronDown className="h-6 w-6 animate-bounce" />
                </button>
            )}
        </>
    );
};

const AdminLoginPage = () => {
    usePageMeta({
        title: "Connexion admin — Atelier AX",
        description: "Connexion à l'espace d'administration Atelier AX.",
        path: "/admin/login",
        noindex: true,
    });
    return <AdminLogin />;
};

const NotFound = () => {
    usePageMeta({
        title: "Page introuvable — Atelier AX",
        description: "La page que vous recherchez n'existe pas ou a été déplacée.",
        noindex: true,
    });
    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-[#FBFBFD] text-neutral-900 px-6 text-center">
            <p className="font-mono-tech text-sm text-neutral-500 mb-3">Erreur 404</p>
            <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mb-4">
                Page introuvable
            </h1>
            <p className="text-neutral-600 mb-8 max-w-md">
                La page que vous recherchez n'existe pas ou a été déplacée.
            </p>
            <Link
                to="/"
                className="dp-btn dp-btn-primary"
            >
                Retour à l'accueil
            </Link>
        </div>
    );
};

function App() {
    return (
        <CartProvider>
            <AdminAuthProvider>
                <BrowserRouter>
                    <Routes>
                        <Route element={<PublicLayout />}>
                            <Route path="/" element={<Landing />} />
                            <Route path="/produits" element={<CatalogPage />} />
                            <Route path="/offres" element={<Offers />} />
                            <Route path="/categorie/:categoryId" element={<CategoryPage />} />
                            <Route path="/produit/:id" element={<ProductDetail />} />
                            <Route path="*" element={<NotFound />} />
                        </Route>
                        <Route path="/admin/login" element={<AdminLoginPage />} />
                        <Route
                            path="/admin/dashboard"
                            element={
                                <AdminRoute>
                                    <AdminDashboard />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/products"
                            element={
                                <AdminRoute>
                                    <AdminProducts />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/products/new"
                            element={
                                <AdminRoute>
                                    <AdminProductForm />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/products/:id/edit"
                            element={
                                <AdminRoute>
                                    <AdminProductForm />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/categories"
                            element={
                                <AdminRoute>
                                    <AdminCategories />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/content"
                            element={
                                <AdminRoute>
                                    <AdminContent />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/settings"
                            element={
                                <AdminRoute>
                                    <AdminSettings />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/quote-requests"
                            element={
                                <AdminRoute>
                                    <AdminQuotes />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/contacts"
                            element={
                                <AdminRoute>
                                    <AdminContacts />
                                </AdminRoute>
                            }
                        />
                        <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                    <FloatingWhatsApp />
                </BrowserRouter>
            </AdminAuthProvider>
        </CartProvider>
    );
}

export default App;
